import { useState } from 'react'

import { toggleArray } from '@domain-group/javascript-utils'

import { useDebounce, useForm } from '@domain-group/react-hooks'

import useAgencies from '@hooks/useAgencies'
import useAgencyOrderBundle from '@hooks/useAgencyOrderBundle'
import useBrandGroups from '@hooks/useBrandGroups'
import useSelector from '@hooks/useSelector'

import type { AgencyOrderBundleModel } from '@models/agencyOrderBundle'

type ToggleAgencyIdParams = {
  agency_ids: number[],
  id?: number,
  setEntityState: Function,
}

const toggleAgencyId = (params: ToggleAgencyIdParams) => {
  const { agency_ids, id, setEntityState } = params

  if (!id) return

  const updatedAssignedAgencies = toggleArray(agency_ids, id)

  setEntityState({
    agency_ids: updatedAssignedAgencies,
  })
}

const requiredFields = [
  { key: 'order_bundle_id' },
]

const defaultSearchState = {
  brand_group_id: undefined,
  brand_id: undefined,
  string: '',
}

const useBulkCreateAgencyOrderBundles = (agencyOrderBundle: AgencyOrderBundleModel) => {
  const { order_bundle_id: orderBundleId, agency_id } = agencyOrderBundle

  const [selectedEntityIds, setSelectedEntityIds] = useState<number[]>([])

  const searchForm = useForm(
    defaultSearchState,
    {
      entity: { order_bundle_id: orderBundleId },
      requiredFields,
      validateOn: 'change',
    },
  )

  const {
    callbacks: { bulkCreateAgencyOrderBundles },
    updating,
  } = useAgencyOrderBundle(agencyOrderBundle)

  const {
    entityState: {
      agency_ids, brand_id, brand_group_id, string,
    },
    setEntityState,
  } = searchForm

  const debouncedString = useDebounce(string, 300)
  const saveEnabled = !!selectedEntityIds.length

  const agenciesPayload = useAgencies({
    filters: { brand_id, brand_group_id, string: debouncedString },
    performHttpRequests: !!brand_id || brand_group_id || debouncedString.length > 1,
    requestOptions: {
      include_agency_assigned_brand_group_ids: true,
    },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencies,
    filteredAgenciesCount,
    loading: loadingAgencies,
  } = agenciesPayload

  const { filteredBrandGroups } = useBrandGroups({ filters: { brand_id } })

  const filteredAgencyIds = filteredAgencies.map(agency => agency.id)

  const entityParams = agency_id
    ? { agency_id, order_bundle_ids: 'TO DO' }
    : { agency_ids: selectedEntityIds, order_bundle_id: orderBundleId }

  const agencies = useSelector(reduxState => reduxState.entities.agencies)

  return {
    agencies,
    callbacks: {
      loadMore,
      bulkCreateAgencyOrderBundles,
      toggleAgencyId: (id?: number) => toggleAgencyId({ agency_ids, id, setEntityState }),
      setSelectedEntityIds,
    },
    canLoadMore,
    debouncedString,
    filteredAgenciesCount,
    filteredAgencyIds,
    filteredBrandGroups,
    entityParams,
    hasActiveFilters: !!brand_id || !!string,
    loadingAgencies,
    saveEnabled,
    searchForm,
    selectedEntityIds,
    updating,
  }
}

export default useBulkCreateAgencyOrderBundles
